import { FC, useEffect, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  AppSidebar,
  Background,
  Button,
  Checkbox,
  ErrorText,
  Input,
  Line,
  LoadingOverlay,
  Select,
} from '@/components';
import { ButtonColor, ButtonSize, Icons } from '@/enums';
import strings from '@/language/Swedish';
import { libraries } from '@/components/map/MapHelpers';
import useBestIndex from '@/api/queries/useBestIndex';
import useIndexSeriesList from '@/api/queries/useIndexSeriesList';
import useIndexPolygonList from '@/api/queries/useIndexPolygonList';
import useIndexesAtPoint, {
  PointIndexType,
} from '@/api/mutations/useIndexesAtPoint';
import { CoordinatesType } from '@/types/dataTypes';
import {
  MAX_COMPARE,
  homeTypeOptions,
  nextFreeColor,
  filterValuesByDateRange,
  rebaseValues,
  computeChangeStats,
  SelectedIndexType,
} from './indexViewerConfig';
import IndexCompareChart, {
  ChartSeriesType,
  IndexCompareChartHandle,
} from './IndexCompareChart';
import IndexMap, { MapPolygonType } from './IndexMap';
import PlaceSearch from './PlaceSearch';
import {
  exportIndexSeriesToCsv,
  exportIndexSeriesToExcel,
} from './exportIndexSeries';

const PageContainer = styled.div`
  ${tw`flex h-screen`};
`;

const Main = styled.main`
  ${tw`relative flex flex-col flex-1 gap-3 overflow-y-auto`};
  padding: 13px;
`;

const ChartCard = styled.div`
  ${tw`flex flex-col gap-2 p-4 rounded-xl border border-white bg-white/80 backdrop-blur-xl shadow-2xl`};
  height: 45%;
  min-height: 20rem;
`;

const ChartHeader = styled.div`
  ${tw`flex flex-wrap items-center gap-2`};
`;

const ChartTitle = styled.span`
  ${tw`flex-1 text-sm font-medium text-slate-800`};
`;

const DateInput = styled(Input)`
  ${tw`h-8 w-36 text-xs`};
`;

const ChartBody = styled.div`
  ${tw`flex flex-col flex-1 min-h-0`};
`;

const ChartFooter = styled.div`
  ${tw`flex items-center justify-between gap-2`};
`;

const FooterGroup = styled.div`
  ${tw`flex items-center gap-2`};
`;

const BaseValueInput = styled(Input)`
  ${tw`h-8 w-20 text-xs`};
`;

const MapContainer = styled.div`
  ${tw`relative flex-1 min-h-72 w-full backdrop-blur-xl bg-transparent`};
`;

const ChooseModeContainer = styled.div`
  ${tw`absolute bottom-6 left-3 z-10 px-3 py-2 rounded-xl border border-white bg-white/90 backdrop-blur shadow-2xl`};
`;

const PointListPanel = styled.div`
  ${tw`absolute top-3 right-3 z-20 w-72 flex flex-col rounded-xl border border-slate-400 bg-white shadow-2xl shadow-slate-900/40 ring-4 ring-slate-900/10 overflow-hidden`};
`;

const PointListHeader = styled.div`
  ${tw`flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200`};
`;

const PointListTitle = styled.span`
  ${tw`text-xs font-medium text-slate-800`};
`;

const PointList = styled.ul`
  ${tw`flex flex-col gap-1 p-2 max-h-64 overflow-y-auto`};
`;

const PointListItem = styled.li`
  ${tw`flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-800/5`};
`;

const PointListName = styled.span`
  ${tw`flex-1 text-xs text-slate-800 truncate`};
`;

const PointListMeta = styled.span`
  ${tw`text-[0.65rem] text-slate-500 whitespace-nowrap`};
`;

const SidebarHeading = styled.span`
  ${tw`text-xs font-medium text-slate-800`};
`;

const SelectedCounter = styled.span<{ $nearMax: boolean; $atMax: boolean }>`
  ${tw`font-normal`};

  ${({ $nearMax, $atMax }) => {
    if ($atMax) return tw`text-red-600 font-medium`;
    if ($nearMax) return tw`text-amber-600 font-medium`;
    return tw`text-slate-500`;
  }};
`;

const FilterGroup = styled.div`
  ${tw`flex flex-col gap-1`};
`;

const FilterLabel = styled.label`
  ${tw`text-xs text-slate-600`};
`;

const IdRow = styled.div`
  ${tw`flex gap-1 items-center`};
`;

const IdInput = styled(Input)`
  ${tw`h-8 text-xs flex-1`};
`;

const SelectedList = styled.ul`
  ${tw`flex flex-col gap-1`};
`;

const SelectedItem = styled.li`
  ${tw`flex items-center gap-2 px-2 py-1 rounded-lg bg-white/70 border border-white cursor-pointer transition`};

  &:hover {
    ${tw`bg-white border-slate-300`};
  }
`;

const ColorPicker = styled.input`
  ${tw`w-4 h-4 flex-none p-0 border-none bg-transparent rounded-full cursor-pointer appearance-none`};

  &::-webkit-color-swatch-wrapper {
    ${tw`p-0`};
  }

  &::-webkit-color-swatch {
    ${tw`border-none rounded-full`};
  }

  &::-moz-color-swatch {
    ${tw`border-none rounded-full`};
  }
`;

const SelectedName = styled.span`
  ${tw`flex-1 text-xs text-slate-800 truncate`};
`;

const InfoMessage = styled.span`
  ${tw`text-xs text-slate-600`};
`;

const EmptyChart = styled.div`
  ${tw`flex flex-1 items-center justify-center text-sm text-slate-500`};
`;

const StatsOverlay = styled.div`
  ${tw`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex flex-col gap-2 px-5 py-4 rounded-2xl border border-slate-400 bg-white shadow-2xl shadow-slate-900/40 ring-4 ring-slate-900/10 pointer-events-none min-w-64`};
`;

const StatsHeading = styled.div`
  ${tw`flex items-center gap-2`};
`;

const StatsDot = styled.i<{ $color: string }>`
  ${tw`w-3 h-3 rounded-full flex-none`};
  background-color: ${({ $color }) => $color};
`;

const StatsTitle = styled.span`
  ${tw`text-sm font-medium text-slate-800 truncate`};
`;

const StatsSubtitle = styled.span`
  ${tw`text-xs text-slate-500`};
`;

const StatsRow = styled.div`
  ${tw`flex items-center justify-between gap-6`};
`;

const StatsLabel = styled.span`
  ${tw`text-xs text-slate-600`};
`;

const StatsValue = styled.span<{ $negative?: boolean }>`
  ${tw`text-sm font-medium tabular-nums`};
  ${({ $negative }) => ($negative ? tw`text-red-700` : tw`text-primary`)};
`;

const IndexViewer: FC = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_API_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
    language: 'sv',
    version: 'quarterly',
  });

  const [homeType, setHomeType] = useState('apartment');
  const [selected, setSelected] = useState<SelectedIndexType[]>([]);
  const [clickedCoordinates, setClickedCoordinates] =
    useState<CoordinatesType>({ lat: null, lng: null });
  const [idInput, setIdInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [baseValue, setBaseValue] = useState('100');
  const chartRef = useRef<IndexCompareChartHandle>(null);
  const [isMaxReached, setIsMaxReached] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mapHoveredId, setMapHoveredId] = useState<string | null>(null);
  const [pointIndexes, setPointIndexes] = useState<PointIndexType[] | null>(
    null
  );
  const [isChooseOnClick, setIsChooseOnClick] = useState(false);

  // In choose-mode the map click opens a picker instead of auto-adding, so the
  // best-index lookup is disabled by passing empty coordinates
  const { data: bestIndexData, isFetching: bestIndexPending } = useBestIndex(
    homeType,
    isChooseOnClick ? { lat: null, lng: null } : clickedCoordinates
  );

  const addIndex = (id: string) => {
    setSelected((previous) => {
      if (previous.some((item) => item.id === id)) return previous;
      if (previous.length >= MAX_COMPARE) {
        setIsMaxReached(true);
        return previous;
      }
      setIsMaxReached(false);
      return [...previous, { id, color: nextFreeColor(previous) }];
    });
  };

  const removeIndex = (id: string) => {
    setSelected((previous) => previous.filter((item) => item.id !== id));
    setIsMaxReached(false);
  };

  const changeIndexColor = (id: string, color: string) => {
    setSelected((previous) =>
      previous.map((item) => (item.id === id ? { ...item, color } : item))
    );
  };

  useEffect(() => {
    if (bestIndexData?.indexId) addIndex(bestIndexData.indexId);
  }, [bestIndexData]);

  const ids = selected.map((item) => item.id);
  const seriesQueries = useIndexSeriesList(ids);
  const polygonQueries = useIndexPolygonList(ids);

  const {
    mutate: listIndexesAtPoint,
    isPending: pointListPending,
  } = useIndexesAtPoint();

  // Pre-fill the date range once data is available: 10 years back → latest value
  useEffect(() => {
    if (dateFrom || dateTo) return;

    const allDates: string[] = [];
    seriesQueries.forEach((query) =>
      query.data?.values?.forEach((value) =>
        allDates.push(String(value.average_date).substring(0, 10))
      )
    );
    if (!allDates.length) return;

    allDates.sort();
    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);

    setDateFrom(tenYearsAgo.toISOString().substring(0, 10));
    setDateTo(allDates[allDates.length - 1]);
  }, [seriesQueries, dateFrom, dateTo]);

  const handlePointSelected = (coordinates: CoordinatesType) => {
    setClickedCoordinates(coordinates);
    setPointIndexes(null);

    if (isChooseOnClick) {
      listIndexesAtPoint(coordinates, {
        onSuccess: (result) => setPointIndexes(result),
      });
    }
  };

  const chartSeries: ChartSeriesType[] = selected.flatMap((item, index) => {
    const data = seriesQueries[index]?.data;
    if (!data?.values) return [];

    const values = filterValuesByDateRange(data.values, dateFrom, dateTo);
    const parsedBase = parseFloat(baseValue) || 100;
    return [
      {
        id: item.id,
        name: data.short_name || item.id,
        color: item.color,
        // With a from-date set, every series starts at the chosen base value
        // on that date so the chart reads as change since then
        values: dateFrom ? rebaseValues(values, parsedBase) : values,
      },
    ];
  });

  const polygons: MapPolygonType[] = selected.map((item, index) => ({
    id: item.id,
    color: item.color,
    geojson: polygonQueries[index]?.data ?? null,
  }));

  const isPending =
    bestIndexPending ||
    pointListPending ||
    seriesQueries.some((query) => query.isFetching);

  const hoveredPosition = selected.findIndex(
    (item) => item.id === mapHoveredId
  );
  const hoveredItem = hoveredPosition >= 0 ? selected[hoveredPosition] : null;
  const hoveredData =
    hoveredPosition >= 0 ? seriesQueries[hoveredPosition]?.data : null;
  const hoveredStats = hoveredData?.values
    ? computeChangeStats(hoveredData.values)
    : null;

  const percentFormat = new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'always',
  });
  const hasError = seriesQueries.some((query) => query.isError);

  const handleAddById = () => {
    const id = idInput.trim();
    if (id) {
      addIndex(id);
      setIdInput('');
    }
  };

  return (
    <PageContainer>
      <Background />
      <AppSidebar currentApp="index">
        <SidebarHeading>{strings.indexApp.searchHeading}</SidebarHeading>
        {isLoaded && <PlaceSearch onPlaceSelected={handlePointSelected} />}
        <FilterGroup>
          <FilterLabel htmlFor="index-home-type">
            {strings.indexApp.homeType}
          </FilterLabel>
          <Select
            id="index-home-type"
            options={homeTypeOptions}
            selectedValue={homeType}
            handleChange={(value) => setHomeType(String(value))}
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel htmlFor="index-id-input">
            {strings.indexApp.indexId}
          </FilterLabel>
          <IdRow>
            <IdInput
              id="index-id-input"
              type="text"
              placeholder={strings.indexApp.indexId}
              value={idInput}
              onChange={(event) => setIdInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleAddById();
              }}
            />
            <Button
              color={ButtonColor.secondary}
              size={ButtonSize.small}
              icon={Icons.plus}
              onClick={handleAddById}
            >
              {strings.indexApp.addById}
            </Button>
          </IdRow>
        </FilterGroup>
        <Line />
        <SidebarHeading>
          {strings.indexApp.selectedIndexes}{' '}
          <SelectedCounter
            $nearMax={selected.length >= MAX_COMPARE - 5}
            $atMax={selected.length >= MAX_COMPARE}
          >
            ({selected.length}/{MAX_COMPARE})
          </SelectedCounter>
        </SidebarHeading>
        {!selected.length && (
          <InfoMessage>{strings.indexApp.empty}</InfoMessage>
        )}
        <SelectedList>
          {selected.map((item, index) => (
            <SelectedItem
              key={item.id}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <ColorPicker
                type="color"
                value={item.color}
                title={strings.indexApp.changeColor}
                onChange={(event) =>
                  changeIndexColor(item.id, event.target.value)
                }
              />
              <SelectedName
                title={seriesQueries[index]?.data?.full_name || item.id}
              >
                {seriesQueries[index]?.data?.short_name || item.id}
              </SelectedName>
              <Button
                color={ButtonColor.transparent}
                size={ButtonSize.small}
                icon={Icons.cross}
                toolTip={strings.indexApp.remove}
                onClick={() => removeIndex(item.id)}
              />
            </SelectedItem>
          ))}
        </SelectedList>
        {isMaxReached && (
          <InfoMessage>
            {strings.indexApp.maxReached.replace('@max', String(MAX_COMPARE))}
          </InfoMessage>
        )}
        {hasError && <ErrorText text={strings.indexApp.error} />}
      </AppSidebar>
      <Main>
        {isPending && <LoadingOverlay />}
        {hoveredItem && hoveredStats && (
          <StatsOverlay>
            <StatsHeading>
              <StatsDot $color={hoveredItem.color} />
              <StatsTitle>
                {hoveredData?.short_name || hoveredItem.id}
              </StatsTitle>
            </StatsHeading>
            <StatsSubtitle>{strings.indexApp.development}</StatsSubtitle>
            {hoveredStats.map((stat) => (
              <StatsRow key={stat.months}>
                <StatsLabel>
                  {stat.months} {strings.indexApp.monthsShort}
                </StatsLabel>
                <StatsValue
                  $negative={stat.percent !== null && stat.percent < 0}
                >
                  {stat.percent === null
                    ? '–'
                    : `${percentFormat.format(stat.percent)} %`}
                </StatsValue>
              </StatsRow>
            ))}
          </StatsOverlay>
        )}
        <ChartCard>
          <ChartHeader>
            <ChartTitle>{strings.indexApp.title}</ChartTitle>
            <FilterLabel htmlFor="index-date-from">
              {strings.indexApp.from}
            </FilterLabel>
            <DateInput
              id="index-date-from"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
            <FilterLabel htmlFor="index-date-to">
              {strings.indexApp.to}
            </FilterLabel>
            <DateInput
              id="index-date-to"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </ChartHeader>
          <ChartBody>
            {chartSeries.length ? (
              <IndexCompareChart
                ref={chartRef}
                series={chartSeries}
                hoveredId={hoveredId}
                onSeriesHover={setHoveredId}
              />
            ) : (
              <EmptyChart>{strings.indexApp.empty}</EmptyChart>
            )}
          </ChartBody>
          <ChartFooter>
            <FooterGroup>
              <FilterLabel htmlFor="index-base-value">
                {strings.indexApp.baseValue}
              </FilterLabel>
              <BaseValueInput
                id="index-base-value"
                type="number"
                min="1"
                value={baseValue}
                onChange={(event) => setBaseValue(event.target.value)}
              />
            </FooterGroup>
            <FooterGroup>
              <Button
                color={ButtonColor.border}
                size={ButtonSize.medium}
                icon={Icons.excel}
                disabled={!chartSeries.length}
                onClick={() =>
                  exportIndexSeriesToExcel(
                    chartSeries,
                    strings.indexApp.exportFileName
                  )
                }
              >
                {strings.indexApp.exportExcel}
              </Button>
              <Button
                color={ButtonColor.border}
                size={ButtonSize.medium}
                icon={Icons.excel}
                disabled={!chartSeries.length}
                onClick={() =>
                  exportIndexSeriesToCsv(
                    chartSeries,
                    strings.indexApp.exportFileName
                  )
                }
              >
                {strings.indexApp.exportCsv}
              </Button>
              <Button
                color={ButtonColor.border}
                size={ButtonSize.medium}
                icon={Icons.report}
                disabled={!chartSeries.length}
                onClick={() =>
                  chartRef.current?.exportPng(strings.indexApp.exportFileName)
                }
              >
                {strings.indexApp.exportPng}
              </Button>
            </FooterGroup>
          </ChartFooter>
        </ChartCard>
        <MapContainer>
          {isLoaded && (
            <IndexMap
              polygons={polygons}
              marker={clickedCoordinates}
              hoveredId={hoveredId}
              onMapClick={handlePointSelected}
              onRemoveIndex={removeIndex}
              onPolygonHover={setMapHoveredId}
            />
          )}
          <ChooseModeContainer>
            <Checkbox
              id="index-choose-on-click"
              label={strings.indexApp.chooseOnClick}
              isChecked={isChooseOnClick}
              onToggle={() => setIsChooseOnClick((previous) => !previous)}
            />
          </ChooseModeContainer>
          {pointIndexes && (
            <PointListPanel>
              <PointListHeader>
                <PointListTitle>
                  {strings.indexApp.pointListTitle}
                </PointListTitle>
                <Button
                  color={ButtonColor.transparent}
                  size={ButtonSize.small}
                  icon={Icons.cross}
                  onClick={() => setPointIndexes(null)}
                />
              </PointListHeader>
              <PointList>
                {!pointIndexes.length && (
                  <InfoMessage>{strings.indexApp.pointListEmpty}</InfoMessage>
                )}
                {pointIndexes.map((item) => (
                  <PointListItem key={item.id}>
                    <PointListName title={item.fullName || item.name}>
                      {item.name}
                    </PointListName>
                    <PointListMeta>
                      {item.homeTypes
                        .map(
                          (homeType) =>
                            strings.indexApp.homeTypeNames[
                              homeType as keyof typeof strings.indexApp.homeTypeNames
                            ] ?? homeType
                        )
                        .join(', ')}
                    </PointListMeta>
                    <Button
                      color={ButtonColor.secondary}
                      size={ButtonSize.small}
                      icon={Icons.plus}
                      toolTip={strings.indexApp.pointListAdd}
                      onClick={() => {
                        addIndex(item.id);
                        setPointIndexes(null);
                      }}
                    />
                  </PointListItem>
                ))}
              </PointList>
            </PointListPanel>
          )}
        </MapContainer>
      </Main>
    </PageContainer>
  );
};

export default IndexViewer;
